
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Upload, X, Loader2, RefreshCw } from 'lucide-react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

interface ImageUploaderProps {
  onUploadComplete: (imageUrl: string) => void;
  category?: "finance" | "housing" | "loans" | "lifestyle" | "energy" | "car" | "boats";
  useStorageBucket?: boolean;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({ 
  onUploadComplete, 
  category = "finance",
  useStorageBucket = true
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type and size
      const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!validTypes.includes(file.type)) {
        toast.error('Virheellinen tiedostomuoto. Salli vain JPEG, PNG ja WebP.');
        return;
      }

      if (file.size > maxSize) {
        toast.error('Tiedosto on liian suuri. Maksimikoko on 5MB.');
        return;
      }

      setSelectedFile(file);
      setUploadProgress(0);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setUploadProgress(10);
      
      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      
      // For Supabase storage bucket
      if (useStorageBucket) {
        // Define folder based on category
        const filePath = category ? `${category}/${fileName}` : fileName;
        
        setUploadProgress(30);
        
        console.log(`Uploading to bucket: article-images, path: ${filePath}`);
        
        const { error: uploadError, data } = await supabase.storage
          .from('article-images')
          .upload(filePath, selectedFile, {
            cacheControl: '3600',
            upsert: true // Changed to true to allow overwriting in case of conflicts
          });

        setUploadProgress(70);

        if (uploadError) {
          console.error("Upload error:", uploadError);
          throw uploadError;
        }

        const { data: urlData } = supabase.storage
          .from('article-images')
          .getPublicUrl(filePath);
          
        console.log("Upload successful, public URL:", urlData.publicUrl);  
          
        setUploadProgress(100);
        toast.success('Kuva ladattu onnistuneesti!');
        onUploadComplete(urlData.publicUrl);
        setSelectedFile(null);
        return;
      }
      
      // Legacy user_images bucket upload (keeping for backward compatibility)
      const filePath = `user_images/${fileName}`;
      
      setUploadProgress(30);

      const { error: uploadError } = await supabase.storage
        .from('user_images')
        .upload(filePath, selectedFile);
        
      setUploadProgress(70);

      if (uploadError) {
        console.error("Legacy upload error:", uploadError);
        throw uploadError;
      }

      const { data } = supabase.storage.from('user_images').getPublicUrl(filePath);
      
      setUploadProgress(100);
      toast.success('Kuva ladattu onnistuneesti!');
      onUploadComplete(data.publicUrl);
      setSelectedFile(null);
    } catch (error) {
      console.error('Kuvan lataaminen epäonnistui:', error);
      toast.error('Kuvan lataaminen epäonnistui. Yritä uudelleen.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setUploadProgress(0);
  };

  return (
    <div className="flex flex-col items-center space-y-4">
      <input 
        type="file" 
        accept="image/jpeg,image/png,image/webp" 
        onChange={handleFileSelect} 
        className="hidden" 
        id="file-upload" 
        aria-label="Valitse kuva tietokoneeltasi"
      />
      
      {!selectedFile && (
        <label 
          htmlFor="file-upload" 
          className="bg-blue-100 text-blue-600 px-4 py-2 rounded-md cursor-pointer hover:bg-blue-200 transition-colors flex items-center"
          title="Valitse kuva tietokoneeltasi ladattavaksi"
        >
          <Upload className="mr-2 h-5 w-5" />
          Valitse kuva tietokoneeltasi
        </label>
      )}

      {selectedFile && (
        <div className="w-full space-y-4">
          <div className="flex items-center space-x-4">
            <span className="text-sm truncate max-w-[200px]" title={selectedFile.name}>
              {selectedFile.name}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={clearSelectedFile}
              className="text-red-500 hover:bg-red-50"
              aria-label="Peruuta kuvan valinta"
              title="Peruuta kuvan valinta"
              disabled={uploading}
            >
              <X className="h-4 w-4 mr-1" /> Peruuta
            </Button>
          </div>
          
          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-blue-600 h-2.5 rounded-full" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          )}
          
          <Button 
            onClick={handleUpload} 
            disabled={uploading}
            className="bg-green-500 hover:bg-green-600 w-full"
            aria-label="Lataa valittu kuva kuvapankkiin"
            title="Lataa valittu kuva kuvapankkiin"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Ladataan kuvatiedostoa...
              </>
            ) : (
              <>Lataa kuva kuvapankkiin</>
            )}
          </Button>
        </div>
      )}
      
      <div className="text-sm text-gray-500 max-w-md text-center mt-4">
        <p>Kuvat tallennetaan Lainasammon kuvapankkiin artikkelien käyttöön.</p>
        <p>Huomioithan, että kuvien tulee olla käyttöoikeuksiltaan sopivia verkkosivulla käytettäväksi.</p>
      </div>
    </div>
  );
};

export default ImageUploader;
